import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NumberUtils } from '../utils';

@Pipe({
  name: 'durationFormatter',
  pure: false,
})
export class DurationFormatterPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(durationInSecond: number): string {
    if (!NumberUtils.isPositiveOrZeroNumber(durationInSecond)) {
      return '-';
    }

    const secondSingular = this.translateService.instant('pipes.time.second');
    const secondPlural = this.translateService.instant('pipes.time.seconds');
    const minuteSingular = this.translateService.instant('pipes.time.minute');
    const minutePlural = this.translateService.instant('pipes.time.minutes');
    const hourSingular = this.translateService.instant('pipes.time.hour');
    const hourPlural = this.translateService.instant('pipes.time.hours');
    const daySingular = this.translateService.instant('pipes.time.day');
    const dayPlural = this.translateService.instant('pipes.time.days');
    const monthSingular = this.translateService.instant('pipes.time.month');
    const monthPlural = this.translateService.instant('pipes.time.months');
    const yearSingular = this.translateService.instant('pipes.time.year');
    const yearPlural = this.translateService.instant('pipes.time.years');

    const secondInAYear = 365 * 24 * 60 * 60;
    const secondInAMonth = 30 * 24 * 60 * 60;
    const secondInADay = 24 * 60 * 60;
    const secondInAnHour = 60 * 60;
    const secondInAMinute = 60;

    const year = Math.floor(durationInSecond / secondInAYear);
    const month = Math.floor(
      (durationInSecond - year * secondInAYear) / secondInAMonth
    );
    const day = Math.floor(
      (durationInSecond - year * secondInAYear - month * secondInAMonth) /
        secondInADay
    );
    const hour = Math.floor(
      (durationInSecond -
        year * secondInAYear -
        month * secondInAMonth -
        day * secondInADay) /
        secondInAnHour
    );
    const minute = Math.floor(
      (durationInSecond -
        year * secondInAYear -
        month * secondInAMonth -
        day * secondInADay -
        hour * secondInAnHour) /
        secondInAMinute
    );
    const second =
      durationInSecond -
      year * secondInAYear -
      month * secondInAMonth -
      day * secondInADay -
      hour * secondInAnHour -
      minute * secondInAMinute;

    let durationAsStr = '';
    if (year > 1) {
      durationAsStr = this.addTextToStr(durationAsStr, `${year} ${yearPlural}`);
    } else if (year > 0) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${year} ${yearSingular}`
      );
    }

    if (month > 1) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${month} ${monthPlural}`
      );
    } else if (month > 0) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${month} ${monthSingular}`
      );
    }

    if (day > 1) {
      durationAsStr = this.addTextToStr(durationAsStr, `${day} ${dayPlural}`);
    } else if (day > 0) {
      durationAsStr = this.addTextToStr(durationAsStr, `${day} ${daySingular}`);
    }

    if (hour > 1) {
      durationAsStr = this.addTextToStr(durationAsStr, `${hour} ${hourPlural}`);
    } else if (hour > 0) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${hour} ${hourSingular}`
      );
    }

    if (minute > 1) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${minute} ${minutePlural}`
      );
    } else if (minute > 0) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${minute} ${minuteSingular}`
      );
    }

    if (second > 1) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${second} ${secondPlural}`
      );
    } else if (second > 0) {
      durationAsStr = this.addTextToStr(
        durationAsStr,
        `${second} ${secondSingular}`
      );
    }

    return durationAsStr;
  }

  private addTextToStr(originalStr: string, text: string): string {
    const separator = originalStr?.length > 0 ? ' ' : '';

    return `${originalStr}${separator}${text}`;
  }
}
